// Empfänger-Kandidaten für "Nachricht an Mieter/Eigentümer" aus dem Ticket-Composer.
// Reine Logik über bereits geladene Roh-Listen (Supabase-Zugriff bleibt in der Komponente) —
// siehe docs/superpowers/specs/2026-08-05-ticket-empfaenger-auswahl-design.md.
// Grund: mehrere Eigentümer/Mieter am Objekt sind der Normalfall, nicht der Fehlerfall —
// `.maybeSingle()` in der Komponente brach dort mit PostgREST-Fehler statt einer Zeile.

import { eigentuemerAnzeigename, type EigentuemerNameFelder } from '../eigentuemer/anzeige';

export type NachrichtTyp = 'Mieter' | 'Eigentümer';

export interface EmpfaengerKandidat {
  id: string;
  name: string;
  email: string;
}

export interface MieterRow {
  id: string;
  vorname?: string | null;
  nachname?: string | null;
  firma_name?: string | null;
  email?: string | null;
}

export interface EigentuemerRow extends EigentuemerNameFelder {
  id: string;
  email?: string | null;
}

// Gleiche E-Mail nur einmal (case-insensitive), Rest alphabetisch nach Name stabil sortiert.
function dedupeSortiert(kandidaten: EmpfaengerKandidat[]): EmpfaengerKandidat[] {
  const gesehen = new Set<string>();
  const ergebnis: EmpfaengerKandidat[] = [];
  for (const k of kandidaten) {
    const key = k.email.toLowerCase();
    if (gesehen.has(key)) continue;
    gesehen.add(key);
    ergebnis.push(k);
  }
  return ergebnis.sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

export function mieterKandidaten(rows: MieterRow[]): EmpfaengerKandidat[] {
  const kandidaten = (rows || [])
    .filter((r) => r.email?.trim())
    .map((r) => {
      const email = r.email!.trim();
      const person = `${r.vorname ?? ''} ${r.nachname ?? ''}`.replace(/\s+/g, ' ').trim();
      const name = r.firma_name?.trim() || person || email;
      return { id: r.id, name, email };
    });
  return dedupeSortiert(kandidaten);
}

export function eigentuemerKandidaten(rows: EigentuemerRow[]): EmpfaengerKandidat[] {
  const kandidaten = (rows || [])
    .filter((r) => r.email?.trim())
    .map((r) => ({ id: r.id, name: eigentuemerAnzeigename(r), email: r.email!.trim() }));
  return dedupeSortiert(kandidaten);
}

// Präziser Tooltip-Text für den deaktivierten Knopf. `anzahlGesamt` ist die Zahl der
// Mieter/Eigentümer VOR dem E-Mail-Filter (0 = niemand hinterlegt, sonst hinterlegt aber ohne Mail).
export function grundText(anzahlGesamt: number, typ: NachrichtTyp): string {
  if (anzahlGesamt <= 0) {
    return `Kein ${typ} am Objekt hinterlegt`;
  }
  if (anzahlGesamt === 1) {
    return `Keine E-Mail-Adresse beim ${typ} hinterlegt`;
  }
  const plural = typ === 'Mieter' ? 'Mietern' : 'Eigentümern';
  return `Keine E-Mail-Adresse bei den ${anzahlGesamt} ${plural} des Objekts hinterlegt`;
}
